/**
 * Customer-facing menu PRICE RESOLUTION — the TypeScript counterpart of the
 * POS pricing engine (apps/pos/lib/blocs/ordering/order_item_operations/
 * item_pricing_service.dart + modifier_pricing_helper.dart). Online ordering
 * MUST resolve the same unit price the POS would, because the server trusts
 * the cart-stored price (orderService sums item.price + modifier.priceDelta —
 * it does not recompute group pricing). Getting this wrong means customers are
 * charged the wrong amount.
 *
 * Priority chain (see docs/MENU_PRICING_ARCHITECTURE.md):
 *   SubgroupReference per-parent override  >  group pricing strategy  >  item price
 *
 * Resolution is driven by `menuGroupPath` — the ROOT→LEAF chain of group ids the
 * customer navigated to reach the item. The first group in that walk with an
 * active (non-'off') strategy wins ("highest branch wins").
 *
 * Pure functions, no React/Firestore. Reusable by ordering, the menu app, and
 * any cart calculation.
 */

import type { GroupPricingStrategy, IGroupSizePrice, IMenuGroup } from './MenuGroup';
import type { IItem } from './Item';
import type { IModifier } from './Modifier';
import type { IModifierGroup } from './ModifierGroup';
import { SubgroupReference } from './SubgroupReference';

/** A size option presented to the customer (group- or item-sourced). */
export interface ResolvedSize {
  readonly name: string;
  readonly price: number;
}

/**
 * The resolved price shape for an item in a given navigation context:
 *  - `flat`  → a single unit price (fixed group price, menu_specific override, or base)
 *  - `sizes` → the customer must pick a size; `source` says whether the sizes
 *              come from the group (size_based strategy) or the item itself.
 *  - `open`  → the customer enters a custom price.
 */
export type ResolvedItemPrice =
  | { readonly kind: 'flat'; readonly unitPrice: number }
  | { readonly kind: 'sizes'; readonly sizes: ResolvedSize[]; readonly source: 'group' | 'item' }
  | { readonly kind: 'open' };

/**
 * WHICH pricing tier decided an item's price, for the bulk price-sheet's
 * "where does this number come from" column. Mirrors the priority chain
 * documented at the top of this file (SubgroupReference override > group
 * strategy > item price), plus the two item-level sub-tiers of `menu_specific`
 * (`menuGroupPrices` vs. an `ItemReference.priceOverride`).
 */
export type ItemPriceSource =
  | { readonly kind: 'group'; readonly menuGroupId: number; readonly strategy: GroupPricingStrategy; readonly viaParentOverride: boolean }
  | { readonly kind: 'menu_group_price'; readonly menuGroupId: number; readonly ancestorPath: readonly number[] | null }
  | { readonly kind: 'item_reference'; readonly menuGroupId: number }
  | { readonly kind: 'item' };

/** A resolved price bundled with the tier that decided it. */
export interface ExplainedItemPrice {
  readonly price: ResolvedItemPrice;
  readonly source: ItemPriceSource;
}

const findGroup = (groups: IMenuGroup[], id: number): IMenuGroup | undefined =>
  groups.find((g) => g.menuGroupId === id);

/** Build the group walk chain (ROOT→LEAF), falling back to a single leaf id. */
function resolveWalkChain(menuGroupPath: number[] | undefined, menuGroupId: number | undefined): number[] {
  if (menuGroupPath && menuGroupPath.length > 0) return menuGroupPath;
  if (menuGroupId != null) return [menuGroupId];
  return [];
}

/**
 * Group-level pricing for an item along its navigation path. Returns `null`
 * when no group supplies an active price (caller falls back to item pricing).
 * Mirrors item_pricing_service.dart `resolveGroupLevelPrice` (incl. the
 * menu_specific "stop the walk on no override" rule).
 */
function resolveGroupLevelPrice(
  item: IItem,
  menuGroupPath: number[] | undefined,
  menuGroupId: number | undefined,
  allMenuGroups: IMenuGroup[],
):
  | { price: { kind: 'flat'; unitPrice: number } | { kind: 'sizes'; sizes: ResolvedSize[] }; source: ItemPriceSource }
  | null {
  const walkChain = resolveWalkChain(menuGroupPath, menuGroupId);
  if (walkChain.length === 0) return null;
  const rootId = walkChain[0];

  for (let i = 0; i < walkChain.length; i++) {
    const group = findGroup(allMenuGroups, walkChain[i]);
    if (!group) continue;

    // Effective strategy: a SubgroupReference on the PARENT (for non-root
    // groups) overrides this group's own strategy.
    let strategy = group.groupPricingStrategy;
    let fixedPrice = group.groupFixedPrice;
    let sizePrices = group.groupSizePrices;
    let viaParentOverride = false;

    if (i > 0) {
      const parent = findGroup(allMenuGroups, walkChain[i - 1]);
      const subRef = SubgroupReference.findForChild(parent?.subgroups ?? [], walkChain[i], rootId);
      if (subRef?.groupPricingStrategyOverride != null && subRef.groupPricingStrategyOverride !== 'off') {
        strategy = subRef.groupPricingStrategyOverride;
        fixedPrice = subRef.groupFixedPriceOverride;
        sizePrices = subRef.groupSizePricesOverride ?? [];
        viaParentOverride = true;
      }
    }

    if (strategy === 'off') continue;

    if (strategy === 'fixed') {
      if (fixedPrice != null && fixedPrice >= 0) {
        return {
          price: { kind: 'flat', unitPrice: fixedPrice },
          source: { kind: 'group', menuGroupId: walkChain[i], strategy, viaParentOverride },
        };
      }
      continue; // invalid fixed price → keep walking
    }
    if (strategy === 'size_based') {
      if (sizePrices && sizePrices.length > 0) {
        return {
          price: { kind: 'sizes', sizes: sizePrices.map((s: IGroupSizePrice) => ({ name: s.name, price: s.price })) },
          source: { kind: 'group', menuGroupId: walkChain[i], strategy, viaParentOverride },
        };
      }
      continue; // empty size list → keep walking
    }
    if (strategy === 'menu_specific') {
      const itemRef = group.items.find((ref) => ref.itemId === item.itemId);
      if (itemRef?.priceOverride != null) {
        return {
          price: { kind: 'flat', unitPrice: itemRef.priceOverride },
          source: { kind: 'group', menuGroupId: walkChain[i], strategy, viaParentOverride },
        };
      }
      // menu_specific with no override for this item: stop the walk and fall
      // through to item-level pricing (POS behavior — does NOT keep walking).
      return null;
    }
  }
  return null;
}

/** True if `storedPath` is a prefix of (or equal to) `navPath`. */
function isAncestorPathCompatible(storedPath: number[], navPath: number[] | undefined): boolean {
  if (!navPath || navPath.length === 0) return true;
  if (storedPath.length > navPath.length) return false;
  return storedPath.every((id, i) => id === navPath[i]);
}

/** Walk LEAF→ROOT looking for an ItemReference.priceOverride for this item. */
function resolveItemReferencePriceFromPath(
  itemId: number,
  menuGroupPath: number[] | undefined,
  menuGroupId: number | undefined,
  allMenuGroups: IMenuGroup[],
): { price: number; menuGroupId: number } | undefined {
  const chain = [...resolveWalkChain(menuGroupPath, menuGroupId)].reverse();
  for (const gid of chain) {
    const group = findGroup(allMenuGroups, gid);
    const ref = group?.items.find((r) => r.itemId === itemId);
    if (ref?.priceOverride != null) return { price: ref.priceOverride, menuGroupId: gid };
  }
  return undefined;
}

/** Item-level `menu_specific` price (menuGroupPrices, then itemRef override, then basePrice). */
function resolveMenuSpecificPrice(
  item: IItem,
  menuGroupPath: number[] | undefined,
  menuGroupId: number | undefined,
  allMenuGroups: IMenuGroup[],
): { unitPrice: number; source: ItemPriceSource } {
  const lookupChain = [...resolveWalkChain(menuGroupPath, menuGroupId)].reverse(); // LEAF→ROOT

  // Tier 1: item.menuGroupPrices with ancestorPath-aware specificity.
  if (item.menuGroupPrices && item.menuGroupPrices.length > 0) {
    for (const gid of lookupChain) {
      let best: { price: number; ancestorPath?: number[] } | null = null;
      let bestPathLen = -1;
      for (const ref of item.menuGroupPrices) {
        if (ref.menuGroupId !== gid) continue;
        if (ref.ancestorPath && ref.ancestorPath.length > 0) {
          if (!isAncestorPathCompatible(ref.ancestorPath, menuGroupPath)) continue;
          if (ref.ancestorPath.length > bestPathLen) {
            best = ref;
            bestPathLen = ref.ancestorPath.length;
          }
        } else if (best == null) {
          best = ref; // legacy flat entry only if no path-bearing match
        }
      }
      if (best) {
        return {
          unitPrice: best.price,
          source: {
            kind: 'menu_group_price',
            menuGroupId: gid,
            ancestorPath: best.ancestorPath && best.ancestorPath.length > 0 ? [...best.ancestorPath] : null,
          },
        };
      }
    }
  }

  // Tier 2: ItemReference.priceOverride walk.
  const override = resolveItemReferencePriceFromPath(item.itemId!, menuGroupPath, menuGroupId, allMenuGroups);
  if (override) return { unitPrice: override.price, source: { kind: 'item_reference', menuGroupId: override.menuGroupId } };

  // Tier 3: basePrice.
  return { unitPrice: item.basePrice ?? 0, source: { kind: 'item' } };
}

/**
 * Resolve how an item should be priced/displayed in a navigation context,
 * AND which pricing tier decided it — for the bulk price-sheet's "where does
 * this number come from" column.
 */
export function explainItemPrice(
  item: IItem,
  menuGroupPath: number[] | undefined,
  menuGroupId: number | undefined,
  allMenuGroups: IMenuGroup[],
): ExplainedItemPrice {
  const group = resolveGroupLevelPrice(item, menuGroupPath, menuGroupId, allMenuGroups);
  if (group) {
    return group.price.kind === 'flat'
      ? { price: { kind: 'flat', unitPrice: group.price.unitPrice }, source: group.source }
      : { price: { kind: 'sizes', sizes: group.price.sizes, source: 'group' }, source: group.source };
  }

  // No active group pricing → item-level pricing.
  switch (item.pricingStrategy) {
    case 'size':
      return {
        price: {
          kind: 'sizes',
          sizes: (item.sizePrice ?? []).map((s) => ({ name: s.name, price: s.price })),
          source: 'item',
        },
        source: { kind: 'item' },
      };
    case 'open':
      return { price: { kind: 'open' }, source: { kind: 'item' } };
    case 'menu_specific': {
      const r = resolveMenuSpecificPrice(item, menuGroupPath, menuGroupId, allMenuGroups);
      return { price: { kind: 'flat', unitPrice: r.unitPrice }, source: r.source };
    }
    case 'base':
    default: {
      const override = resolveItemReferencePriceFromPath(item.itemId!, menuGroupPath, menuGroupId, allMenuGroups);
      return override
        ? { price: { kind: 'flat', unitPrice: override.price }, source: { kind: 'item_reference', menuGroupId: override.menuGroupId } }
        : { price: { kind: 'flat', unitPrice: item.basePrice ?? 0 }, source: { kind: 'item' } };
    }
  }
}

/**
 * Resolve how an item should be priced/displayed in a navigation context.
 * This is the single entry point the ordering UI should use instead of reading
 * item.basePrice / item.sizePrice directly.
 */
export function resolveItemPrice(
  item: IItem,
  menuGroupPath: number[] | undefined,
  menuGroupId: number | undefined,
  allMenuGroups: IMenuGroup[],
): ResolvedItemPrice {
  return explainItemPrice(item, menuGroupPath, menuGroupId, allMenuGroups).price;
}

/**
 * Collapse a {@link ResolvedItemPrice} into the single unit price that gets
 * STORED on the cart line.
 *
 * This is the last step before a number becomes what the customer is charged —
 * the server sums `item.price` and does not recompute group pricing — so every
 * surface that writes a cart line MUST collapse the resolved price the same
 * way. Extracted from the ordering app so the web cart, the assistant toolset,
 * and anything added later cannot drift from each other.
 *
 * @param selectedSize the size the customer actually chose (name is matched
 *                     against the resolved sizes; `price` is the fallback for
 *                     item-sourced sizes and open pricing)
 */
export function resolveCartUnitPrice(
  item: IItem,
  resolved: ResolvedItemPrice,
  selectedSize?: { readonly name: string; readonly price?: number },
): number {
  if (resolved.kind === 'flat') {
    // Group fixed / menu_specific / item base+override — overrides item base.
    return resolved.unitPrice;
  }
  if (resolved.kind === 'sizes') {
    // Size-based (group or item): use the resolved price for the chosen size
    // (matched by name), falling back to the selected size's own price.
    const match = selectedSize
      ? resolved.sizes.find((s) => s.name === selectedSize.name)
      : undefined;
    return match?.price ?? selectedSize?.price ?? item.basePrice ?? 0;
  }
  // Open price — operator/customer-entered value flows via selectedSize.
  return selectedSize?.price ?? item.basePrice ?? 0;
}

/**
 * Just enough of an ITEM to read its own references to modifier groups — the
 * item is the closest parent a modifier group has.
 */
export interface ItemModifierLinks {
  readonly modifierGroups?: ReadonlyArray<{
    readonly modifierGroupId: number;
    readonly modifierPriceOverrides?: ReadonlyArray<{ readonly modifierId: number; readonly price: number }>;
  }> | null;
  /** The item's own option prices on ONE menu (`IItem.menuGroupModifierPrices`). */
  readonly menuGroupModifierPrices?: ReadonlyArray<{
    readonly menuGroupId: number;
    readonly modifierGroupId: number;
    readonly modifierId: number;
    readonly price: number;
  }> | null;
}

/** The item's own price for each option of a group, from its reference to that group. */
function itemOverridesFor(item: ItemModifierLinks | null | undefined, modifierGroupId: number): ReadonlyMap<number, number> {
  const out = new Map<number, number>();
  const ref = (item?.modifierGroups ?? []).find((r) => r.modifierGroupId === modifierGroupId);
  for (const o of ref?.modifierPriceOverrides ?? []) {
    if (typeof o?.modifierId === 'number' && typeof o.price === 'number' && Number.isFinite(o.price)) out.set(o.modifierId, o.price);
  }
  return out;
}

/**
 * The item's own prices for each option of a group ON the first menu of the
 * ROOT→LEAF chain that carries any (`item.menuGroupModifierPrices`) — all of
 * that menu's entries, like a menu's whole map. Empty off every such menu.
 */
function itemMenuOverridesFor(
  item: ItemModifierLinks | null | undefined,
  modifierGroupId: number,
  chain: readonly number[],
): ReadonlyMap<number, number> {
  const out = new Map<number, number>();
  const entries = (item?.menuGroupModifierPrices ?? []).filter(
    (e) =>
      e?.modifierGroupId === modifierGroupId &&
      typeof e.menuGroupId === 'number' &&
      typeof e.modifierId === 'number' &&
      typeof e.price === 'number' &&
      Number.isFinite(e.price),
  );
  if (entries.length === 0) return out;
  for (const gid of chain) {
    const here = entries.filter((e) => e.menuGroupId === gid);
    if (here.length === 0) continue;
    for (const e of here) out.set(e.modifierId, e.price);
    return out;
  }
  return out;
}

/**
 * Map of modifierId → overridden price for a modifier group, before the group's
 * own per-option price and the option's own price. Most specific first:
 *
 *   1. the ITEM's own price ON A MENU (`item.menuGroupModifierPrices`): the
 *      first menu on the ROOT→LEAF path that carries entries for the group —
 *      all of them. A wine's own Happy Hour Glass (owner, 2026-10-16:
 *      "bottles and glass can have individual pricing for happy hour");
 *   2. the first menu group on the ROOT→LEAF path whose reference to the group
 *      carries `modifierPriceOverrides` — its whole map (unchanged), the one
 *      price for every item sold through that menu;
 *   3. the ITEM's own reference to the group
 *      (`item.modifierGroups[].modifierPriceOverrides`) — the wine list's
 *      shared "Wine Serving" group, where each wine prices Glass and Bottle on
 *      its own link (design 2026-10-16).
 *
 * Each step answers only for the options it prices; the next one fills the
 * rest. Null when none prices anything. With no item-level price stored this
 * is byte-identical to the menu-only walk. Mirrors the till's
 * `resolveParentPriceOverrides`; pinned by `itemModifierPrice.cases.json`.
 */
export function resolveModifierPriceOverrides(
  modifierGroupId: number,
  menuGroupPath: number[] | undefined,
  allMenuGroups: IMenuGroup[],
  item?: ItemModifierLinks | null,
): Map<number, number> | null {
  const map = new Map<number, number>(itemOverridesFor(item, modifierGroupId));
  const chain = resolveWalkChain(menuGroupPath, undefined); // ROOT→LEAF
  for (const gid of chain) {
    const group = findGroup(allMenuGroups, gid);
    const ref = group?.modifierGroups.find((r) => r.modifierGroupId === modifierGroupId);
    if (ref?.modifierPriceOverrides && ref.modifierPriceOverrides.length > 0) {
      for (const o of ref.modifierPriceOverrides) map.set(o.modifierId, o.price);
      break;
    }
  }
  for (const [id, price] of itemMenuOverridesFor(item, modifierGroupId, chain)) map.set(id, price);
  return map.size > 0 ? map : null;
}

/**
 * Whether an option of a group is offered on an item at all. Every option of
 * an ordinary group is (channel and stock rules aside). An option of a
 * `pricedByItem` group is offered ONLY when the item prices it on its own
 * reference to the group — so a wine sold by the bottle only simply prices
 * the Bottle, and a lost price takes the choice away instead of ringing it at
 * the option's own (absent) price. Mirrors the till's `modifierOfferedOnItem`;
 * pinned by `itemModifierPrice.cases.json`.
 */
export function modifierOfferedOnItem(
  group: { readonly modifierGroupId?: number; readonly pricedByItem?: boolean } | null | undefined,
  modifierId: number | null | undefined,
  item: ItemModifierLinks | null | undefined,
): boolean {
  if (group?.pricedByItem !== true) return true;
  if (typeof modifierId !== 'number' || typeof group.modifierGroupId !== 'number') return false;
  return itemOverridesFor(item, group.modifierGroupId).has(modifierId);
}

/**
 * Resolve the unit price for a single modifier:
 *   parent menu-group override  >  per-group priceOverride (individual)  >  base price
 * Mirrors modifier_pricing_helper.dart `resolveModifierPrice`.
 */
export function resolveModifierUnitPrice(
  modifier: IModifier,
  modifierGroup: Pick<IModifierGroup, 'pricingStrategy'> | undefined,
  parentPriceOverrides: Map<number, number> | null,
): number {
  if (parentPriceOverrides && modifier.modifierId != null) {
    const override = parentPriceOverrides.get(modifier.modifierId);
    if (override !== undefined) return override;
  }
  const strategy = modifierGroup?.pricingStrategy;
  const isIndividual = typeof strategy === 'string' && strategy.toLowerCase().includes('individual');
  if (isIndividual && modifier.priceOverride != null) return modifier.priceOverride;
  return modifier.price ?? 0;
}
